import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { ReceivingResponse } from "../../app/models/responseModels/receivingResponse";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import ReceivingAutoCompleteFilter from "./ReceivingAutoCompleteFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";
import {
  RECEIVING_COLUMN_NAME,
  RECEIVING_FILTER_COLUMN_OPTIONS,
  RECEIVING_LABEL_NAME,
} from "./ReceivingConstants";

const COLUMN_WITH_DATE = ["arrivalDate", "packageDeliveryDate"];

type ReceivingDataFilterProps = {
  receivingData: ReceivingResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<ReceivingResponse[]>>;
  filteredData: ReceivingResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const ReceivingDataFilter = ({
  receivingData,
  setFilteredData,
  filteredData,
}: ReceivingDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>("");

  const [clientNameOptions, setClientNameOptions] = useState<string[]>([]); //Dropdown Options
  const [clientNameSelectedOptions, setClientNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [parcelCenterOptions, setParcelCenterOptions] = useState<string[]>([]); //Dropdown Options
  const [parcelCenterSelectedOptions, setParcelCenterSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [fdpOptions, setFdpOptions] = useState<string[]>([]); //Dropdown Options
  const [fdpSelectedOptions, setFdpSelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [statusOptions, setStatusOptions] = useState<string[]>([]); //Dropdown Options
  const [statusSelectedOptions, setStatusSelectedOptions] = useState<string[]>(
    []
  ); //Dropdown selected value

  const [workAreaOptions, setWorkAreaOptions] = useState<string[]>([]); //Dropdown Options
  const [workAreaSelectedOptions, setWorkAreaSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [sidOptions, setSidOptions] = useState<string[]>([]); //Dropdown Options
  const [sidSelectedOptions, setSidSelectedOptions] = useState<string[]>([]); //Dropdown selected value

  const [arrivalFromDate, setArrivalFromDate] = useState<Date | null>(null);
  const [arrivalToDate, setArrivalToDate] = useState<Date | null>(null);

  const [deliveryFromDate, setDeliveryFromDate] = useState<Date | null>(null);
  const [deliveryToDate, setDeliveryToDate] = useState<Date | null>(null);

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
        return clientNameOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
        return fdpOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
        return statusOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
        return workAreaOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
        return sidOptions;
      }
      return [];
    },
    [
      clientNameOptions,
      parcelCenterOptions,
      fdpOptions,
      statusOptions,
      workAreaOptions,
      sidOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
        return clientNameSelectedOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        return parcelCenterSelectedOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
        return fdpSelectedOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
        return statusSelectedOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
        return workAreaSelectedOptions;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
        return sidSelectedOptions;
      }
      return [];
    },
    [
      clientNameSelectedOptions,
      parcelCenterSelectedOptions,
      fdpSelectedOptions,
      statusSelectedOptions,
      workAreaSelectedOptions,
      sidSelectedOptions,
    ]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
        return arrivalFromDate;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
        return deliveryFromDate;
      }
      return null;
    },
    [arrivalFromDate, deliveryFromDate]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
        return arrivalToDate;
      }
      if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
        return deliveryToDate;
      }
      return null;
    },
    [arrivalToDate, deliveryToDate]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //ClientName dropdown options
    if (selectedColumnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
      setClientNameOptions(
        receivingData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setClientNameOptions(
        filteredData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Parcel Center dropdown options
    if (
      selectedColumnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
    ) {
      setParcelCenterOptions(
        receivingData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterOptions(
        filteredData
          .map((v) => v.parcelCenterLocationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //FDP dropdown options
    if (selectedColumnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
      setFdpOptions(
        receivingData
          .map((v) => v.fdpCompanyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setFdpOptions(
        filteredData
          .map((v) => v.fdpCompanyName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Status dropdown options
    if (selectedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
      setStatusOptions(
        receivingData
          .map((v) => v.packageStatus)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setStatusOptions(
        filteredData
          .map((v) => v.packageStatus)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //WorkArea dropdown options
    if (selectedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
      setWorkAreaOptions(
        receivingData
          .map((v) => v.packageWorkArea)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setWorkAreaOptions(
        filteredData
          .map((v) => v.packageWorkArea)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Sid dropdown options
    if (selectedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
      setSidOptions(
        receivingData
          .map((v) => v.packageSid)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setSidOptions(
        filteredData
          .map((v) => v.packageSid)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, receivingData]);

  useEffect(() => {
    let newData = [...receivingData];
    //ClientName Filter
    if (clientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return clientNameSelectedOptions.some((s) => {
          return s === a.clientName;
        });
      });
    }

    //Parcel Center Filter
    if (parcelCenterSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterSelectedOptions.some((s) => {
          return s === a.parcelCenterLocationName;
        });
      });
    }

    //FDP Filter
    if (fdpSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return fdpSelectedOptions.some((s) => {
          return s === a.fdpCompanyName;
        });
      });
    }

    //Status Filter
    if (statusSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return statusSelectedOptions.some((s) => {
          return s === a.packageStatus;
        });
      });
    }

    //Tracking Filter
    if (trackingNumberFilter) {
      if (trackingNumberFilter.includes(",")) {
        const trackingNumbers = trackingNumberFilter.split(",");
        newData = newData.filter((a) => {
          return trackingNumbers.some((s) => {
            return s === a.trackingNumber;
          });
        });
      } else {
        newData = newData.filter((x) =>
          x.trackingNumber
            .toLowerCase()
            .includes(trackingNumberFilter.toLowerCase())
        );
      }
    }

    if (arrivalFromDate && arrivalToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.arrivalDate).toLocaleDateString() >=
            arrivalFromDate.toLocaleDateString() &&
          new Date(x.arrivalDate).toLocaleDateString() <=
            arrivalToDate.toLocaleDateString()
      );
    }

    if (deliveryFromDate && deliveryToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.packageDeliveryDate).toLocaleDateString() >=
            deliveryFromDate.toLocaleDateString() &&
          new Date(x.packageDeliveryDate).toLocaleDateString() <=
            deliveryToDate.toLocaleDateString()
      );
    }

    //WorkArea Filter
    if (workAreaSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return workAreaSelectedOptions.some((s) => {
          return s === a.packageWorkArea;
        });
      });
    }

    //Sid Filter
    if (sidSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return sidSelectedOptions.some((s) => {
          return s === a.packageSid;
        });
      });
    }

    setFilteredData(newData);
  }, [
    receivingData,
    setFilteredData,
    trackingNumberFilter,
    clientNameSelectedOptions,
    parcelCenterSelectedOptions,
    fdpSelectedOptions,
    statusSelectedOptions,
    arrivalFromDate,
    arrivalToDate,
    deliveryFromDate,
    deliveryToDate,
    workAreaSelectedOptions,
    sidSelectedOptions,
  ]);

  // ***** Handlers *****
  const handleTrackingNumberChange = (e: any) => {
    let trackingNumbers = e.target.value;
    if (
      trackingNumbers.includes(" ") ||
      trackingNumbers.includes("\t") ||
      trackingNumbers.includes(",")
    ) {
      setTrackingNumberFilter(
        trackingNumbers.split(" ").join(",").split("\t").join(",")
      );
    } else {
      setTrackingNumberFilter(trackingNumbers);
    }
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions([]);
    }
    if (
      removedColumnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME
    ) {
      return setParcelCenterSelectedOptions([]);
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
      return setFdpSelectedOptions([]);
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
      return setStatusSelectedOptions([]);
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
      return setWorkAreaSelectedOptions([]);
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
      return setSidSelectedOptions([]);
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
      setArrivalFromDate(null);
      setArrivalToDate(null);
      return;
    }
    if (removedColumnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
      setDeliveryFromDate(null);
      setDeliveryToDate(null);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
        setClientNameSelectedOptions([]);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
        setParcelCenterSelectedOptions([]);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
        setFdpSelectedOptions([]);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
        setStatusSelectedOptions([]);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
        setArrivalFromDate(null);
        setArrivalToDate(null);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
        setDeliveryFromDate(null);
        setDeliveryToDate(null);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
        setWorkAreaSelectedOptions([]);
      }
      if (oldColumnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
        setSidSelectedOptions([]);
      }
    }
    if (value === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
      newInputList[index].displayColumnName = RECEIVING_LABEL_NAME.CLIENT_NAME;
    }
    if (value === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      newInputList[index].displayColumnName =
        RECEIVING_LABEL_NAME.PARCEL_CENTER_LOCATION_NAME;
    }
    if (value === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
      newInputList[index].displayColumnName =
        RECEIVING_LABEL_NAME.FDP_COMPANY_NAME;
    }
    if (value === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
      newInputList[index].displayColumnName =
        RECEIVING_LABEL_NAME.PACKAGE_STATUS;
    }
    if (value === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
      newInputList[index].displayColumnName = "Arrival";
    }
    if (value === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
      newInputList[index].displayColumnName = "Scheduled Delivery";
    }
    if (value === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
      newInputList[index].displayColumnName =
        RECEIVING_LABEL_NAME.PACKAGE_WORK_AREA;
    }
    if (value === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
      newInputList[index].displayColumnName = RECEIVING_LABEL_NAME.PACKAGE_SID;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setTrackingNumberFilter("");
    setClientNameSelectedOptions([]);
    setParcelCenterSelectedOptions([]);
    setFdpSelectedOptions([]);
    setStatusSelectedOptions([]);
    setArrivalFromDate(null);
    setArrivalToDate(null);
    setDeliveryFromDate(null);
    setDeliveryToDate(null);
    setWorkAreaOptions([]);
    setSidOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === RECEIVING_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PARCEL_CENTER_LOCATION_NAME) {
      return setParcelCenterSelectedOptions(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.FDP_COMPANY_NAME) {
      return setFdpSelectedOptions(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_STATUS) {
      return setStatusSelectedOptions(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_WORK_AREA) {
      return setWorkAreaSelectedOptions(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_SID) {
      return setSidSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
      return setArrivalFromDate(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
      return setDeliveryFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === RECEIVING_COLUMN_NAME.ARRIVAL_DATE) {
      return setArrivalToDate(value);
    }
    if (columnName === RECEIVING_COLUMN_NAME.PACKAGE_DELIVERY_DATE) {
      return setDeliveryToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Receiving Packages</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {RECEIVING_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <ReceivingAutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Tracking Number Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"trackingNumber"}
                  margin="normal"
                  id="trackingNumber"
                  label="Tracking Number"
                  name="trackingNumber"
                  size="small"
                  fullWidth
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ReceivingDataFilter;
